import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { getCourtStartTime, setCourtStartTime, getCrewSchedule, saveCrewSchedule } from "../firebaseService";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];
const CREW_LABELS = ["A", "B", "C", "D"];
const CREW_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2"];

const CourtStartTimesSettings = () => {
  const navigate = useNavigate();
  const { daysOfWeek } = useTournamentConfig();
  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);
  const [courtConfigs, setCourtConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [mode, setMode] = useState("simple"); // "simple" or "flexible"

  // Flexible crew schedule state
  const [crewCount, setCrewCount] = useState(2);
  const [crewSlots, setCrewSlots] = useState({}); // { "A": [{ court, from, to }], "B": [...], ... }
  const [copyFromDay, setCopyFromDay] = useState("");

  const loadDay = useCallback(async (day) => {
    setLoading(true);

    // Load simple configs
    const configs = {};
    await Promise.all(
      courts.map(async (court) => {
        const data = await getCourtStartTime(day, court);
        configs[court] = {
          startTime: data.startTime || "",
          hasThreeCrews: data.hasThreeCrews || false,
          isTurnusEnabled: data.isTurnusEnabled || false,
        };
      })
    );
    setCourtConfigs(configs);

    // Load flexible crew schedule
    const schedule = await getCrewSchedule(day);
    if (schedule && schedule.mode) {
      // Respect saved mode preference
      setMode(schedule.mode);
    } else if (schedule && schedule.slots && Object.keys(schedule.slots).length > 0) {
      // Legacy: flexible schedule exists without mode field
      setMode("flexible");
    } else {
      setMode("simple");
    }

    if (schedule) {
      setCrewCount(schedule.crewCount || 2);
      setCrewSlots(schedule.slots || {});
    } else {
      // Initialize empty slots for default crew count
      const emptySlots = {};
      CREW_LABELS.slice(0, 2).forEach((label) => {
        emptySlots[label] = [];
      });
      setCrewSlots(emptySlots);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadDay(selectedDay);
  }, [selectedDay, loadDay]);

  // --- Simple mode handlers ---
  const handleChange = (court, field, value) => {
    setCourtConfigs((prev) => ({
      ...prev,
      [court]: { ...prev[court], [field]: value },
    }));
  };

  const handleSaveCourt = async (court) => {
    setSaving(court);
    try {
      const config = courtConfigs[court];
      await Promise.all([
        setCourtStartTime(selectedDay, court, config.startTime, config.hasThreeCrews, config.isTurnusEnabled),
        // Persist mode preference so loadDay restores it correctly
        saveCrewSchedule(selectedDay, {
          crewCount,
          slots: crewSlots,
          courts: courts,
          mode: "simple",
        }),
      ]);
      setSnackbar({ open: true, message: `${court} gespeichert!`, severity: "success" });
    } catch {
      setSnackbar({ open: true, message: `Fehler beim Speichern von ${court}`, severity: "error" });
    }
    setSaving(null);
  };

  const handleSaveAllSimple = async () => {
    setSaving("all");
    try {
      await Promise.all([
        // Save all court configs
        ...courts.map((court) => {
          const config = courtConfigs[court];
          if (!config?.startTime) return Promise.resolve();
          return setCourtStartTime(selectedDay, court, config.startTime, config.hasThreeCrews, config.isTurnusEnabled);
        }),
        // Persist mode preference so loadDay restores it correctly
        saveCrewSchedule(selectedDay, {
          crewCount,
          slots: crewSlots,
          courts: courts,
          mode: "simple",
        }),
      ]);
      setSnackbar({ open: true, message: "Alle Courts gespeichert!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Fehler beim Speichern", severity: "error" });
    }
    setSaving(null);
  };

  // --- Flexible mode handlers ---
  const handleCrewCountChange = (count) => {
    setCrewCount(count);
    const newSlots = { ...crewSlots };
    CREW_LABELS.slice(0, count).forEach((label) => {
      if (!newSlots[label]) newSlots[label] = [];
    });
    // Remove extra crews
    CREW_LABELS.slice(count).forEach((label) => {
      delete newSlots[label];
    });
    setCrewSlots(newSlots);
  };

  const handleAddSlot = (crew) => {
    setCrewSlots((prev) => ({
      ...prev,
      [crew]: [...(prev[crew] || []), { court: courts[0], from: "10:00", to: "11:00" }],
    }));
  };

  const handleUpdateSlot = (crew, index, field, value) => {
    setCrewSlots((prev) => {
      const slots = [...(prev[crew] || [])];
      slots[index] = { ...slots[index], [field]: value };
      return { ...prev, [crew]: slots };
    });
  };

  const handleRemoveSlot = (crew, index) => {
    setCrewSlots((prev) => ({
      ...prev,
      [crew]: (prev[crew] || []).filter((_, i) => i !== index),
    }));
  };

  const handleSaveFlexible = async () => {
    setSaving("flexible");
    try {
      await saveCrewSchedule(selectedDay, {
        crewCount,
        slots: crewSlots,
        courts: courts,
        mode: "flexible",
      });
      setSnackbar({ open: true, message: `Crew-Einsatzplan fuer ${selectedDay} gespeichert!`, severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Fehler beim Speichern", severity: "error" });
    }
    setSaving(null);
  };

  const handleCopyFromDay = async () => {
    if (!copyFromDay) return;
    const schedule = await getCrewSchedule(copyFromDay);
    if (schedule) {
      setCrewCount(schedule.crewCount || 2);
      setCrewSlots(schedule.slots || {});
      setSnackbar({ open: true, message: `Plan von ${copyFromDay} uebernommen (noch nicht gespeichert)`, severity: "info" });
    } else {
      setSnackbar({ open: true, message: `Kein Crew-Plan fuer ${copyFromDay} vorhanden`, severity: "warning" });
    }
    setCopyFromDay("");
  };

  // Preview: generate timeline
  const getTimelinePreview = () => {
    const allSlots = [];
    Object.entries(crewSlots).forEach(([crew, slots]) => {
      (slots || []).forEach((slot) => {
        allSlots.push({ crew, ...slot });
      });
    });

    if (allSlots.length === 0) return null;

    // Collect all unique time points
    const times = new Set();
    allSlots.forEach((s) => {
      times.add(s.from);
      times.add(s.to);
    });
    const sortedTimes = Array.from(times).sort();

    // Build 30-min timeline from earliest to latest
    const earliest = sortedTimes[0];
    const latest = sortedTimes[sortedTimes.length - 1];
    const [eh, em] = earliest.split(":").map(Number);
    const [lh, lm] = latest.split(":").map(Number);

    const timeline = [];
    let cur = eh * 60 + em;
    const end = lh * 60 + lm;
    while (cur < end) {
      const hh = String(Math.floor(cur / 60)).padStart(2, "0");
      const mm = String(cur % 60).padStart(2, "0");
      timeline.push(`${hh}:${mm}`);
      cur += 30;
    }

    return { timeline, allSlots };
  };

  const timeToMinutes = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const getSlotForCrewAtTime = (crew, timeStr, allSlots) => {
    const t = timeToMinutes(timeStr);
    const slot = allSlots.find(
      (s) => s.crew === crew && timeToMinutes(s.from) <= t && timeToMinutes(s.to) > t
    );
    return slot;
  };

  // Simple mode preview
  const getPreviewSlots = (config) => {
    if (!config?.startTime) return [];
    const [h, m] = config.startTime.split(":").map(Number);
    const interval = config.isTurnusEnabled ? 45 : 60;
    const totalCrews = config.hasThreeCrews ? 3 : 2;
    const slots = [];
    const current = new Date();
    current.setHours(h, m, 0, 0);
    const endTime = new Date();
    endTime.setHours(20, 0, 0, 0);

    while (current <= endTime) {
      const hh = String(current.getHours()).padStart(2, "0");
      const mm = String(current.getMinutes()).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
      current.setMinutes(current.getMinutes() + interval);
    }
    return { slots, totalCrews };
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Crew-Einsatzplanung
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Konfiguriere Crews, Plaetze und Einsatzzeiten pro Tag
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate("../court_crew_times")}
          >
            Crew-Zeiten Uebersicht
          </Button>
          <Chip
            label="Einfach"
            color={mode === "simple" ? "primary" : "default"}
            onClick={() => setMode("simple")}
            variant={mode === "simple" ? "filled" : "outlined"}
          />
          <Chip
            label="Flexibel (Crews teilen Plaetze)"
            color={mode === "flexible" ? "primary" : "default"}
            onClick={() => setMode("flexible")}
            variant={mode === "flexible" ? "filled" : "outlined"}
          />
        </Box>
      </Box>

      {/* Tag-Auswahl */}
      <Tabs
        value={selectedDay}
        onChange={(_, v) => setSelectedDay(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {daysOfWeek.map((day) => (
          <Tab key={day} label={day} value={day} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : mode === "simple" ? (
        /* ========================================
           SIMPLE MODE (existing behavior)
           ======================================== */
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAllSimple} disabled={saving !== null}>
              Alle speichern
            </Button>
          </Box>
          <Grid container spacing={2}>
            {courts.map((court) => {
              const config = courtConfigs[court] || {};
              const preview = getPreviewSlots(config);

              return (
                <Grid item xs={12} md={6} key={court}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h6">{court}</Typography>
                        <Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={() => handleSaveCourt(court)} disabled={saving !== null}>
                          {saving === court ? "..." : "Speichern"}
                        </Button>
                      </Box>

                      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
                        <TextField
                          label="Startzeit"
                          type="time"
                          value={config.startTime || ""}
                          onChange={(e) => handleChange(court, "startTime", e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ width: 140 }}
                        />
                        <FormControlLabel
                          control={<Switch checked={config.hasThreeCrews || false} onChange={(e) => handleChange(court, "hasThreeCrews", e.target.checked)} size="small" />}
                          label="3 Crews"
                        />
                        <FormControlLabel
                          control={<Switch checked={config.isTurnusEnabled || false} onChange={(e) => handleChange(court, "isTurnusEnabled", e.target.checked)} size="small" />}
                          label="45min Turnus"
                        />
                      </Box>

                      {config.startTime && preview.slots && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                            Vorschau: {preview.totalCrews} Crews, {config.isTurnusEnabled ? "45" : "60"}min Intervall
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflow: "auto" }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Zeit</TableCell>
                                  {preview.slots.map((t) => (
                                    <TableCell key={t} sx={{ py: 0.5, fontSize: "0.75rem" }}>{t}</TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Array.from({ length: preview.totalCrews }, (_, i) => {
                                  const crewLabel = CREW_LABELS[i];
                                  return (
                                    <TableRow key={crewLabel}>
                                      <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Crew {crewLabel}</TableCell>
                                      {preview.slots.map((_, slotIdx) => (
                                        <TableCell
                                          key={slotIdx}
                                          sx={{
                                            py: 0.5,
                                            fontSize: "0.7rem",
                                            bgcolor: slotIdx % preview.totalCrews === i ? "primary.main" : "transparent",
                                            color: slotIdx % preview.totalCrews === i ? "white" : "text.secondary",
                                            textAlign: "center",
                                          }}
                                        >
                                          {slotIdx % preview.totalCrews === i ? "Einsatz" : "Pause"}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}

                      {!config.startTime && (
                        <Typography variant="body2" color="text.secondary">Keine Startzeit gesetzt</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : (
        /* ========================================
           FLEXIBLE MODE (crews sharing courts)
           ======================================== */
        <Box>
          {/* Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Anzahl Crews</Typography>
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                  {[2, 3, 4].map((n) => (
                    <Chip
                      key={n}
                      label={`${n} Crews`}
                      color={crewCount === n ? "primary" : "default"}
                      onClick={() => handleCrewCountChange(n)}
                      variant={crewCount === n ? "filled" : "outlined"}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Select value={copyFromDay} onChange={(e) => setCopyFromDay(e.target.value)} size="small" displayEmpty sx={{ minWidth: 150 }}>
                  <MenuItem value="" disabled>Plan kopieren von...</MenuItem>
                  {daysOfWeek.filter((d) => d !== selectedDay).map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
                <IconButton onClick={handleCopyFromDay} disabled={!copyFromDay} color="primary" size="small">
                  <ContentCopyIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveFlexible} disabled={saving !== null}>
                {saving === "flexible" ? "Speichert..." : "Plan speichern"}
              </Button>
            </Box>
          </Paper>

          {/* Crew Slot Editors */}
          <Grid container spacing={2}>
            {CREW_LABELS.slice(0, crewCount).map((crew, crewIdx) => (
              <Grid item xs={12} md={6} key={crew}>
                <Card sx={{ borderLeft: `4px solid ${CREW_COLORS[crewIdx]}` }}>
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="h6" sx={{ color: CREW_COLORS[crewIdx], fontWeight: 700 }}>
                        Crew {crew}
                      </Typography>
                      <Chip
                        label={`${(crewSlots[crew] || []).length} Einsaetze`}
                        size="small"
                        sx={{ bgcolor: CREW_COLORS[crewIdx], color: "white" }}
                      />
                    </Box>

                    {(crewSlots[crew] || []).map((slot, idx) => (
                      <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                        <Select
                          value={slot.court}
                          onChange={(e) => handleUpdateSlot(crew, idx, "court", e.target.value)}
                          size="small"
                          sx={{ minWidth: 140, flex: 1 }}
                        >
                          {courts.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                          ))}
                        </Select>
                        <TextField
                          type="time"
                          value={slot.from}
                          onChange={(e) => handleUpdateSlot(crew, idx, "from", e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 110 }}
                          label="Von"
                        />
                        <TextField
                          type="time"
                          value={slot.to}
                          onChange={(e) => handleUpdateSlot(crew, idx, "to", e.target.value)}
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 110 }}
                          label="Bis"
                        />
                        <IconButton size="small" color="error" onClick={() => handleRemoveSlot(crew, idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      onClick={() => handleAddSlot(crew)}
                      sx={{ mt: 0.5 }}
                    >
                      Einsatz hinzufuegen
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Timeline Preview */}
          {(() => {
            const preview = getTimelinePreview();
            if (!preview) return null;

            return (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Tagesansicht</Typography>
                <TableContainer sx={{ maxWidth: "100%", overflow: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 2, minWidth: 70 }}>Crew</TableCell>
                        {preview.timeline.map((t) => (
                          <TableCell key={t} align="center" sx={{ minWidth: 60, fontSize: "0.75rem", fontWeight: 600 }}>{t}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {CREW_LABELS.slice(0, crewCount).map((crew, crewIdx) => (
                        <TableRow key={crew}>
                          <TableCell sx={{ fontWeight: 700, color: CREW_COLORS[crewIdx], position: "sticky", left: 0, bgcolor: "#1a2138", zIndex: 1 }}>
                            Crew {crew}
                          </TableCell>
                          {preview.timeline.map((t) => {
                            const slot = getSlotForCrewAtTime(crew, t, preview.allSlots);
                            return (
                              <TableCell
                                key={t}
                                align="center"
                                sx={{
                                  fontSize: "0.65rem",
                                  bgcolor: slot ? CREW_COLORS[crewIdx] : "transparent",
                                  color: slot ? "white" : "text.disabled",
                                  py: 0.5,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {slot ? slot.court.replace("Court ", "C") : "---"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })()}
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CourtStartTimesSettings;
